// This is an internal module, so we import directly from the library's dist folder.
// TypeScript may not find type declarations for this, so we use a require statement.
import { request } from "fhirclient/lib/lib";

const CACHE_PREFIX = "fhir-client-cache-";
const CACHE_TTL = 6 * 60 * 60 * 1000; // 6 hours in milliseconds
const JWKS_URI_CACHE_PREFIX = "fhir-client-jwks-uri-cache";
const ISSUER_CACHE_PREFIX = "fhir-client-issuer-cache";

interface CacheEntry {
  expires: number;
  data: any;
}

/**
 * A custom implementation of getAndCache that uses localStorage.
 * This function will replace the original one in the fhirclient library.
 * @param url The URL to request
 * @param requestOptions Request options
 * @param force If true, reload from source and update the cache
 */
export function getAndCacheWithLocalStorage(
  url: string,
  requestOptions?: RequestInit,
  force = false
): Promise<any> {
  const cacheKey = CACHE_PREFIX + url;

  if (force) {
    try {
      localStorage.removeItem(cacheKey);
    } catch (e) {
      console.error("Error removing from localStorage cache", e);
    }
  }
  // Check localStorage
  if (!force) {
    try {
      const cachedItem = localStorage.getItem(cacheKey);
      if (cachedItem) {
        const { expires, data } = JSON.parse(cachedItem) as CacheEntry;
        if (expires > Date.now()) {
          const promise = Promise.resolve(data);
          return promise;
        } else {
          // Clear expired item
          localStorage.removeItem(cacheKey);
        }
      }
    } catch (e) {
      console.error("Error reading from localStorage cache", e);
    }
  }

  // 3. Fetch from network
  const requestPromise = request(url, requestOptions);

  // After resolution, store the data in localStorage
  requestPromise
    .then((data) => {
      const expires = Date.now() + CACHE_TTL;
      localStorage.setItem(cacheKey, JSON.stringify({ expires, data }));

      // Check if the response contains jwks_uri and cache it separately
      if (data && typeof data === "object" && (data as any).jwks_uri) {
        const jwksUriEntry = {
          data: (data as any).jwks_uri,
        };
        localStorage.setItem(
          JWKS_URI_CACHE_PREFIX,
          JSON.stringify(jwksUriEntry)
        );
      }
      // Check if the response contains issuer and cache it separately
      if (data && typeof data === "object" && (data as any).issuer) {
        const issuerEntry = {
          data: (data as any).issuer,
        };
        localStorage.setItem(ISSUER_CACHE_PREFIX, JSON.stringify(issuerEntry));
      }
    })
    .catch((e) => {
      console.error("Error writing to localStorage cache", e);
    });

  return requestPromise;
}

function getFromCache(cacheKey: string): any {
  try {
    const cachedItem = localStorage.getItem(cacheKey);
    if (cachedItem) {
      const { data } = JSON.parse(cachedItem) as CacheEntry;
      return data;
    }
  } catch (e) {
    console.error("Error reading jwks_uri from cache", e);
  }
  return null;
}

export const getCachedJwksUri = (): any => {
  return getFromCache(JWKS_URI_CACHE_PREFIX);
};

export const getCachedIssuer = (): any => {
  return getFromCache(ISSUER_CACHE_PREFIX);
};
